import type { ReactNode } from 'react';

import { Footer } from '@shared/chrome/Footer';
import { copy } from '@shared/copy';
import { useThemeStore } from '@shared/theme/ThemeProvider';
import { BrandLockup } from '@shared/ui/icons/BrandMark';
import { MoonIcon, SunIcon } from '@shared/ui/icons';

import { authCopy } from './copy';

/**
 * The half shell the three pre-session screens carry — and the half it drops is
 * the half that assumes a session.
 *
 * Every other route in this console composes the whole chrome. Here the
 * navigation handle would open a column of seventeen destinations none of which
 * will open, the search field submits to the content list, the create action
 * routes into authoring, and the account menu names a signed-in operator and
 * offers a sign-out. Drawing any of them would say the shell does not know who
 * is looking at it.
 *
 * What survives is what needs no session: the skip link, the lockup, the theme
 * toggle, and the footer — whose About and Contact entries are mandatory on
 * every public **and gated** surface, which is why a sign-in screen has them.
 *
 * **The lockup is a `<span>`, not a link.** The console root needs a session, so
 * a link there bounces the reader back to the screen they are standing on, and
 * the public host is not the operator's destination. The wordmark is
 * **un-clipped** here: the measurement that clips it is about a bar carrying six
 * controls, and this one carries one.
 *
 * There is no breadcrumb, and no `aria-current` anywhere: the URL an operator
 * landed on is not a destination in the console's column, and marking the
 * nearest entry would announce a location the reader is not at.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  const theme = useThemeStore((state) => state.theme);
  const toggleTheme = useThemeStore((state) => state.toggleTheme);

  return (
    <div className="flex min-h-dvh flex-col">
      <a className="skip-link" href="#main">
        {copy.skipToContent}
      </a>

      <header role="banner" className="flex items-center gap-3 px-gutter py-3">
        {/* The full lockup at every width, and deliberately NOT a link: §3.1's
            one departure. Everywhere else the lockup carries the operator to
            `index.html`; that route needs a session and would return them to
            the screen they are standing on, so here it is an inert span. */}
        <BrandLockup
          className="flex shrink-0 items-center gap-2 text-ink"
          label={authCopy.shell.brandLabel}
        />

        <span className="ms-auto" />

        <button
          type="button"
          onClick={toggleTheme}
          aria-pressed={theme === 'light'}
          aria-label={theme === 'dark' ? copy.theme.toLight : copy.theme.toDark}
          className="hit-target flex shrink-0 items-center justify-center rounded-sm hover:bg-hover-bg"
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
      </header>

      {/*
        The column grows to fill what is between the bar and the footer, and the
        card centres in it on both axes. The form is short, and leaving it stuck
        under the bar above an empty page reads as a half-loaded screen.
      */}
      {/*
        The column stops at 26 rem on a phone and opens to 30 rem from
        `--bp-lg` — the ceiling the design system already declares for a field.
        Wider makes the input wider than the system recommends; narrower leaves
        a desktop holding a phone-sized card.
      */}
      <div className="grid flex-1 grow content-center justify-items-center px-gutter py-7 md:py-9">
        <main id="main" className="w-full max-w-[26rem] lg:max-w-[30rem]">
          {children}
        </main>
      </div>

      <Footer />
    </div>
  );
}

/**
 * The heading and its lede.
 *
 * The heading takes `--fs-h2` at the base tier and `--fs-h1` from `--bp-md`: a
 * full-size `h1` above a 26 rem card swallows a phone screen before the reader
 * reaches the first field, and a screen that has the room carries the whole
 * title without pushing that field under the fold.
 *
 * The alignment is `start`, never centred — a centred Arabic column tires the
 * reading.
 */
export function AuthHead({ title, children }: { title: string; children: ReactNode }) {
  return (
    <header className="mb-5 grid gap-stack-tight">
      <h1 className="text-h2 md:text-h1">{title}</h1>
      <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{children}</p>
    </header>
  );
}

/**
 * The card the form sits in — the design system's `.card`, and its body
 * breathes from `--bp-md` where the screen can afford it.
 */
export function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-surface">
      <div className="p-5 md:p-6">{children}</div>
    </div>
  );
}
